/**
 * HTTP layer for the SSN Card verification SDK.
 *
 * Stateless request helpers: create a verification by uploading the card
 * image(s), then poll for the result. The backend routes `documentType:
 * ssn_card` + `provider: regula` + `submissionMode: UPLOAD` through Regula for
 * server-side recognition.
 *
 * Self-contained module (own types, own endpoint resolution) — does not import
 * any legal-id flow logic.
 */

import { ApiError } from "./verification";
import type {
  SsnCardVerificationRequest,
  SsnCardCreateResponse,
  SsnCardVerificationResponse,
} from "../types/ssnCardVerification";
import { addAPIBreadcrumb } from "../utils/sentryBreadcrumbs";
import { trackAPIError } from "../utils/sentryTracking";
import { getSsnCardVerificationApiBaseUrl } from "../utils/apiConfig";
import { logger } from "../utils/logger";

/** Flow tag used in breadcrumbs / Sentry context for this SDK. */
const FLOW = "ssn_card_verification";

/** Provider + submission mode that route the request through Regula. */
const SSN_CARD_PROVIDERS = ["regula"] as const;
const SSN_CARD_SUBMISSION_MODE = "UPLOAD" as const;

/**
 * Verification endpoint path — the dedicated document verification API
 * (POST/GET /v1/verifications/documents). This is the non-legal-ID document API:
 * a Legal ID mirror minus the PATCH, with a `documentType` routing field.
 */
export const SSN_CARD_VERIFICATION_PATH = "/v1/verifications/documents";

/**
 * Document type routing key on the create request. The documents API uses this
 * (alongside `provider: regula` + `submissionMode: UPLOAD`) to route the upload
 * through Regula for server-side SSN card recognition.
 */
export const SSN_CARD_DOCUMENT_TYPE = "ssn" as const;

/**
 * Create an SSN card verification by uploading the front + back cropped images.
 *
 * POSTs the two-sided Regula `UPLOAD` payload and returns the id to poll.
 */
export async function createSsnCardVerification(
  params: SsnCardVerificationRequest
): Promise<SsnCardCreateResponse> {
  const endpoint = `POST ${SSN_CARD_VERIFICATION_PATH}`;
  logger.log("[API] Creating SSN card verification");

  const requestBody = {
    documentType: SSN_CARD_DOCUMENT_TYPE,
    providers: [...SSN_CARD_PROVIDERS],
    submissionMode: SSN_CARD_SUBMISSION_MODE,
    externalReferenceId: params.externalReferenceId,
    media: {
      imageFront: params.imageFrontBase64,
      imageBack: params.imageBackBase64,
    },
  };

  addAPIBreadcrumb(endpoint, "pending", { flow: FLOW });

  let response: Response;
  try {
    response = await fetch(`${getSsnCardVerificationApiBaseUrl()}${SSN_CARD_VERIFICATION_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${params.sessionToken}`,
        "X-Client-Type": "web-sdk",
      },
      body: JSON.stringify(requestBody),
    });
  } catch (networkError) {
    addAPIBreadcrumb(endpoint, "network_error", { error: String(networkError) });
    trackAPIError(networkError, endpoint, { flow: FLOW });
    throw networkError;
  }

  if (!response.ok) {
    throw await buildApiError(response, endpoint, "Failed to create SSN card verification");
  }

  let responseData: any;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error("Failed to parse SSN card verification response");
    addAPIBreadcrumb(endpoint, "parse_error", { error: String(parseError) });
    trackAPIError(error, endpoint, { flow: FLOW });
    throw error;
  }

  if (!responseData?.uuid) {
    throw new Error("Invalid SSN card verification response - missing uuid");
  }

  addAPIBreadcrumb(endpoint, response.status, { verificationId: responseData.uuid });
  return { verificationId: responseData.uuid };
}

/**
 * Poll the SSN card verification status.
 *
 * Returns the raw status payload plus the HTTP status code (mirrors the
 * legal-id status helper's return shape so the controller can backoff/poll).
 */
export async function getSsnCardVerificationStatus(
  verificationId: string,
  sessionToken: string
): Promise<{ data: SsnCardVerificationResponse; statusCode: number }> {
  const endpoint = `GET ${SSN_CARD_VERIFICATION_PATH}/${verificationId}`;
  logger.log("[API] Getting SSN card verification status:", verificationId);

  addAPIBreadcrumb(endpoint, "pending", { verificationId });

  let response: Response;
  try {
    response = await fetch(`${getSsnCardVerificationApiBaseUrl()}${SSN_CARD_VERIFICATION_PATH}/${verificationId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${sessionToken}`,
      },
    });
  } catch (networkError) {
    addAPIBreadcrumb(endpoint, "network_error", { verificationId, error: String(networkError) });
    trackAPIError(networkError, endpoint, { verificationId });
    throw networkError;
  }

  if (!response.ok) {
    throw await buildApiError(response, endpoint, "Failed to get SSN card verification status", {
      verificationId,
    });
  }

  let responseData: SsnCardVerificationResponse;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error("Failed to parse SSN card verification status response");
    addAPIBreadcrumb(endpoint, "parse_error", { error: String(parseError) });
    trackAPIError(error, endpoint, { verificationId });
    throw error;
  }

  logger.log("[API] SSN card verification status retrieved successfully.");
  addAPIBreadcrumb(endpoint, response.status, {
    verificationId,
    status: responseData.status,
    outcome: responseData.outcome,
  });

  return { data: responseData, statusCode: response.status };
}

/**
 * Read an error response body and build a typed {@link ApiError}, preferring the
 * backend's `detail`/`description` message and `status` field for the code.
 */
async function buildApiError(
  response: Response,
  endpoint: string,
  prefix: string,
  context: Record<string, unknown> = {}
): Promise<ApiError> {
  let errorMessage = `${response.status} ${response.statusText}`;
  let errorData: any = null;
  try {
    errorData = await response.json();
    if (errorData.description) errorMessage = errorData.description;
    if (errorData.detail) errorMessage = errorData.detail;
  } catch {
    /* ignore parse errors */
  }

  const statusCode = errorData?.status ?? response.status;
  const error = new ApiError(`${prefix}: ${errorMessage}`, statusCode, errorData);
  addAPIBreadcrumb(endpoint, statusCode, { ...context, errorMessage });
  trackAPIError(error, endpoint, { flow: FLOW, ...context, httpStatus: statusCode, errorData });
  return error;
}
